/**
 * Single source of truth for allowed file types in chat and document uploads.
 * Only PDF and text types are supported (no other application/* types).
 */

/** MIME types allowed for chat/document attachments: PDF and text/* only */
export const CHAT_ALLOWED_MIME_TYPES = [
  "application/pdf",
  "text/plain",
  "text/markdown",
  "text/csv",
  "text/html",
  "text/xml",
  "text/css",
] as const;

/**
 * Accept string for file input (accept attribute).
 * Includes MIME types and explicit extensions so OS file dialogs show the correct filter
 * (e.g. Windows may misparse MIME-only strings).
 */
/** Extension-first accept string — more reliable in OS file dialogs than MIME-only lists. */
export const CHAT_ACCEPT_EXTENSIONS =
  ".pdf,.txt,.md,.markdown,.csv,.log,.html,.htm,.xml,.css";

export const CHAT_ACCEPT_STRING =
  `${CHAT_ACCEPT_EXTENSIONS},application/pdf,text/plain,text/markdown,text/csv,text/html,text/xml,text/css`;

const EXTENSION_MIME: Record<string, string> = {
  ".pdf": "application/pdf",
  ".txt": "text/plain",
  ".md": "text/markdown",
  ".markdown": "text/markdown",
  ".csv": "text/csv",
  ".log": "text/plain",
  ".html": "text/html",
  ".htm": "text/html",
  ".xml": "text/xml",
  ".css": "text/css",
};

/**
 * Returns true if the given MIME type is allowed for chat/document attachments.
 * Allows application/pdf and any text/* type.
 */
export function isAllowedChatFileType(mimeType: string): boolean {
  if (mimeType === "application/pdf") return true;
  if (mimeType.startsWith("text/")) return true;
  return false;
}

/**
 * Resolves a usable MIME type for upload validation. Browsers often leave
 * {@code File.type} empty for .md/.txt/.csv — fall back to extension.
 */
export function resolveChatFileMimeType(file: Pick<File, "name" | "type">): string {
  if (file.type && isAllowedChatFileType(file.type)) {
    return file.type;
  }
  const lower = file.name.toLowerCase();
  const dot = lower.lastIndexOf(".");
  if (dot >= 0) {
    const ext = lower.slice(dot);
    const fromExt = EXTENSION_MIME[ext];
    if (fromExt) return fromExt;
  }
  return file.type || "";
}

/** True when the file is allowed by MIME or by known extension fallback. */
export function isAllowedChatFile(file: Pick<File, "name" | "type">): boolean {
  return isAllowedChatFileType(resolveChatFileMimeType(file));
}
